import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Trash2, Check, Loader2 } from "lucide-react";
import { API } from "../lib/brand";

/* ============================================================
   /admin/photos
   Password-gated admin page to generate Nano Banana images,
   review the pending grid, assign a slot, approve or reject.
   ============================================================ */

const SLOT_PRESETS = [
  { key: "home_hero",          label: "Home hero" },
  { key: "helios_hero",        label: "Helios hero" },
  { key: "brain_hero",         label: "Brain hero" },
  { key: "factory_hero",       label: "Factory hero" },
  { key: "research_hero",      label: "Research hero" },
  { key: "framework_hero",     label: "Framework hero" },
  { key: "company_hero",       label: "Company hero" },
  { key: "industry_banking",        label: "Industry · Banking" },
  { key: "industry_banking_inner",  label: "Industry · Banking (inner)" },
  { key: "industry_sovereign",      label: "Industry · Sovereign funds" },
  { key: "industry_sovereign_inner",label: "Industry · Sovereign funds (inner)" },
  { key: "industry_real_estate",    label: "Industry · Real estate" },
  { key: "industry_real_estate_inner", label: "Industry · Real estate (inner)" },
  { key: "industry_insurance",      label: "Industry · Insurance" },
  { key: "industry_insurance_inner",label: "Industry · Insurance (inner)" },
  { key: "industry_healthcare",     label: "Industry · Healthcare" },
  { key: "industry_healthcare_inner", label: "Industry · Healthcare (inner)" },
  { key: "industry_energy",         label: "Industry · Energy" },
  { key: "industry_energy_inner",   label: "Industry · Energy (inner)" },
  { key: "industry_manufacturing",  label: "Industry · Manufacturing" },
  { key: "industry_manufacturing_inner", label: "Industry · Manufacturing (inner)" },
  { key: "industry_public_sector",  label: "Industry · Public sector" },
  { key: "industry_public_sector_inner", label: "Industry · Public sector (inner)" },
  { key: "industry_telecom",        label: "Industry · Telecom" },
  { key: "industry_telecom_inner",  label: "Industry · Telecom (inner)" },
  { key: "industry_retail",         label: "Industry · Retail" },
  { key: "industry_retail_inner",   label: "Industry · Retail (inner)" },
  { key: "industry_maritime",       label: "Industry · Maritime" },
  { key: "industry_maritime_inner", label: "Industry · Maritime (inner)" },
  { key: "industry_defense",        label: "Industry · Defense" },
  { key: "industry_defense_inner",  label: "Industry · Defense (inner)" },
  { key: "industry_space",          label: "Industry · Space" },
  { key: "industry_space_inner",    label: "Industry · Space (inner)" },
  { key: "industry_robotics",       label: "Industry · Robotics" },
  { key: "industry_robotics_inner", label: "Industry · Robotics (inner)" },
  { key: "industry_physical",       label: "Industry · Physical AI" },
  { key: "industry_physical_inner", label: "Industry · Physical AI (inner)" },
];

const PROMPT_TEMPLATES = [
  {
    name: "Helios. Discovery / data ingestion",
    body: "Dark cinematic editorial image, 21:9 widescreen. Abstract data discovery visualization: a vast dark gray (#141414) space with subtle luminous data signals streaming inward from every direction toward a glowing dim core. Architectural depth, brutalist minimalism, premium editorial. A single warm orange (#FF6600) point of light at the focal core, otherwise strictly monochrome black, white and gray. Volumetric atmosphere, soft falloff, shallow depth of field. Palantir Foundry and DeepMind visual language. No people, no logos, no text, no faces. Hyper-detailed.",
  },
  {
    name: "Brain. Reasoning / scenario simulation",
    body: "Dark cinematic editorial image, 21:9 widescreen. Vast dark gray (#141414) atmospheric space. A subtle web of branching counterfactual paths, illuminated faintly in white, fanning out from a central convergence point. Each branch traced with luminous precision but quickly fading into volumetric haze. A single warm orange (#FF6600) point along the dominant branch. Strictly monochrome otherwise. Architectural calm, brutalist scale, premium editorial. Palantir/DeepMind/Anthropic visual restraint. No people, no logos, no text. Hyper-detailed.",
  },
  {
    name: "Factory. Composable substrate",
    body: "Dark cinematic editorial image, 21:9 widescreen. A vast dark gray (#141414) industrial cathedral interior, an abstract factory of intelligence. Modular cuboid forms suspended in volumetric haze, faintly outlined with white edges. The composition centred around a single illuminated cube glowing warm orange (#FF6600). Cinematic depth, brutalist minimalism, premium editorial. Strictly monochrome otherwise. No people, no logos, no text, no faces. Hyper-detailed.",
  },
  {
    name: "Research. Governance / ledger",
    body: "Dark cinematic editorial image, 21:9 widescreen. Vast dark gray (#141414) atmospheric space. A long horizontal chain of identical cuboid 'ledger blocks' floating in a row, faint white hairline edges, deeply receding into volumetric haze. Each block linked to the next by a barely visible bright thread. A subtle warm orange (#FF6600) seal glowing softly on the rightmost block. Brutalist scale, premium editorial. Strictly monochrome otherwise. No people, no logos, no text. Hyper-detailed.",
  },
  {
    name: "Framework. Pipeline / through-line",
    body: "Dark cinematic editorial image, 21:9 widescreen. A vast dark gray (#141414) corridor receding deep into volumetric haze. Six identical luminous gates evenly spaced along a single perspectival axis, each gate slightly brighter than the previous. A faint warm orange (#FF6600) signal travels through the gates. Architectural calm, brutalist minimalism, premium editorial. Strictly monochrome otherwise. Palantir/DeepMind visual restraint. No people, no logos, no text. Hyper-detailed.",
  },
  {
    name: "Industry. Banking",
    body: "Dark cinematic editorial image, 21:9 widescreen. Vast dark gray (#141414) atmospheric space. Abstract financial topology: thousands of points of light at varying depths, connected by faint white threads suggesting a vast portfolio graph. Subtle warm orange (#FF6600) glow at one decision node. Strictly monochrome otherwise. Brutalist scale, premium editorial. Palantir Foundry visual language. No people, no logos, no text. Hyper-detailed.",
  },
];

const TOKEN_KEY = "helios.admin.photoToken";

function useToken() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const save = useCallback((t) => {
    setToken(t);
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);
  return [token, save];
}

async function api(path, opts = {}, token) {
  const r = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": token,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`${r.status}: ${t}`);
  }
  if (r.status === 204) return null;
  return r.json();
}

function TokenGate({ onUnlock }) {
  const [val, setVal] = useState("");
  return (
    <main className="min-h-screen flex items-center justify-center px-6" style={{ background: "#141414" }}>
      <form
        onSubmit={(e) => { e.preventDefault(); onUnlock(val); }}
        className="glass-card p-10 max-w-md w-full"
      >
        <div className="eyebrow-flame mb-5">Admin · Photos</div>
        <h1 className="display text-white mb-6" style={{ fontSize: 32, fontWeight: 500, lineHeight: 1.1 }}>
          Enter admin token<br />
          <span className="italic-accent">to manage photos.</span>
        </h1>
        <input
          autoFocus
          type="password"
          className="hb-input mb-6"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="X-Admin-Token"
          data-testid="admin-token-input"
        />
        <button
          type="submit"
          className="hb-pill hb-pill-light w-full justify-center"
          data-testid="admin-token-submit"
        >
          <span className="hb-pill-icon">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M9 6l6 6-6 6"/></svg>
          </span>
          Unlock
        </button>
      </form>
    </main>
  );
}

function PhotoCard({ photo, token, onChange }) {
  const [slot, setSlot] = useState(photo.slot || SLOT_PRESETS[0].key);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await api(`/admin/photos/${photo.id}/approve`, {
        method: "POST",
        body: JSON.stringify({ slot }),
      }, token);
      toast.success(`Approved → ${slot}`);
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Reject and delete this photo permanently?")) return;
    setBusy(true);
    try {
      await api(`/admin/photos/${photo.id}/reject`, { method: "POST" }, token);
      toast.success("Rejected & deleted");
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const isApproved = photo.status === "approved";

  return (
    <div className="glass-card overflow-hidden flex flex-col" data-testid={`admin-photo-${photo.id}`}>
      <div className="relative" style={{ aspectRatio: "21/9", background: "#0c0c0c" }}>
        <img
          src={`${API}/photos/${photo.id}/image`}
          alt={photo.theme}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 glass-badge tone-live">
          {photo.theme}
        </div>
        <div className={`absolute top-3 right-3 glass-badge ${isApproved ? "tone-flame" : ""}`}>
          {photo.status}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-4">
        {isApproved && (
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF6600]">
            Slot: {photo.slot}
          </div>
        )}
        <p className="text-zinc-400 text-[12px] leading-relaxed line-clamp-3" style={{ fontWeight: 300 }}>
          {photo.prompt}
        </p>

        <div className="flex flex-col gap-3 mt-auto">
          <select
            value={slot}
            onChange={(e) => setSlot(e.target.value)}
            className="hb-input"
            style={{ paddingTop: 10, paddingBottom: 10, borderBottomColor: "rgba(255,255,255,0.18)" }}
            data-testid={`admin-photo-slot-${photo.id}`}
          >
            {SLOT_PRESETS.map((s) => (
              <option key={s.key} value={s.key} style={{ background: "#141414" }}>
                {s.label}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={approve}
              disabled={busy}
              className="hb-pill hb-pill-light flex-1 justify-center disabled:opacity-50"
              data-testid={`admin-photo-approve-${photo.id}`}
            >
              <span className="hb-pill-icon">
                <Check size={12} strokeWidth={2.5} color="#fff" />
              </span>
              {isApproved ? "Re-assign" : "Approve"}
            </button>
            <button
              type="button"
              onClick={reject}
              disabled={busy}
              className="hb-pill hb-pill-ghost px-3 disabled:opacity-50"
              data-testid={`admin-photo-reject-${photo.id}`}
              aria-label="Reject"
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GenerateForm({ token, onCreated }) {
  const [theme, setTheme] = useState("Helios hero");
  const [prompt, setPrompt] = useState(PROMPT_TEMPLATES[0].body);
  const [count, setCount] = useState(1);
  const [aspect, setAspect] = useState("21:9");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const created = await api("/admin/photos/generate", {
        method: "POST",
        body: JSON.stringify({ theme, prompt, count: Number(count), aspect }),
      }, token);
      toast.success(`Generated ${created.length} photo${created.length === 1 ? "" : "s"}`);
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass-card p-6 md:p-8" data-testid="admin-generate-form">
      <div className="eyebrow-flame mb-5">Generate · Nano Banana (Gemini 3.1)</div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Theme */}
        <div className="md:col-span-4">
          <label className="eyebrow block mb-2">Theme label</label>
          <input
            className="hb-input"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            data-testid="admin-gen-theme"
            required
          />
        </div>

        {/* Count */}
        <div className="md:col-span-2">
          <label className="eyebrow block mb-2">Count</label>
          <input
            type="number"
            min={1} max={4}
            className="hb-input"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="admin-gen-count"
          />
        </div>

        {/* Aspect */}
        <div className="md:col-span-2">
          <label className="eyebrow block mb-2">Aspect</label>
          <select
            className="hb-input"
            value={aspect}
            onChange={(e) => setAspect(e.target.value)}
            style={{ paddingTop: 14, paddingBottom: 14 }}
            data-testid="admin-gen-aspect"
          >
            {["21:9", "16:9", "4:3", "1:1", "3:2"].map((a) => (
              <option key={a} value={a} style={{ background: "#141414" }}>{a}</option>
            ))}
          </select>
        </div>

        {/* Template picker */}
        <div className="md:col-span-4">
          <label className="eyebrow block mb-2">Quick template</label>
          <select
            className="hb-input"
            onChange={(e) => {
              const t = PROMPT_TEMPLATES.find((x) => x.name === e.target.value);
              if (t) { setPrompt(t.body); setTheme(t.name); }
            }}
            defaultValue=""
            style={{ paddingTop: 14, paddingBottom: 14 }}
            data-testid="admin-gen-template"
          >
            <option value="" style={{ background: "#141414" }}>pick a template</option>
            {PROMPT_TEMPLATES.map((t) => (
              <option key={t.name} value={t.name} style={{ background: "#141414" }}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Prompt */}
        <div className="md:col-span-12">
          <label className="eyebrow block mb-2">Prompt</label>
          <textarea
            rows={6}
            className="hb-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="admin-gen-prompt"
            style={{ minHeight: 140 }}
            required
          />
        </div>

        <div className="md:col-span-12 flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="hb-pill hb-pill-flame disabled:opacity-50"
            data-testid="admin-gen-submit"
          >
            <span className="hb-pill-icon">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={12} color="#fff" />}
            </span>
            {busy ? `Generating ${count} image${count === "1" ? "" : "s"}…` : `Generate ${count} image${count === "1" ? "" : "s"}`}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function AdminPhotosPage() {
  const [token, setToken] = useToken();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | pending | approved

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const list = await api(`/admin/photos${filter !== "all" ? `?status=${filter}` : ""}`, {}, token);
      setPhotos(list);
    } catch (e) {
      toast.error(e.message);
      if (e.message.startsWith("401")) setToken("");
    } finally {
      setLoading(false);
    }
  }, [token, filter, setToken]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <TokenGate onUnlock={setToken} />;

  return (
    <main data-testid="admin-photos-page" className="min-h-screen px-6 py-16" style={{ background: "#141414" }}>
      <div className="hb-container">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <div className="eyebrow-flame mb-3">Admin · Photo studio</div>
            <h1 className="display text-white" style={{ fontSize: "clamp(34px, 4.5vw, 56px)", fontWeight: 500, letterSpacing: "-0.025em", lineHeight: 1.0 }}>
              Generate, review,{" "}
              <span className="italic-accent">approve.</span>
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setToken("")}
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white"
            data-testid="admin-signout"
          >
            Sign out
          </button>
        </div>

        <div className="mb-10">
          <GenerateForm token={token} onCreated={load} />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex gap-2">
            {["all", "pending", "approved"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`nf-tab ${filter === f ? "active" : ""}`}
                data-testid={`admin-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-white flex items-center gap-2"
            data-testid="admin-refresh"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>

        {photos.length === 0 ? (
          <div className="glass-card p-16 text-center">
            <p className="text-zinc-400">No photos yet. Generate one with the form above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {photos.map((p) => (
              <PhotoCard key={p.id} photo={p} token={token} onChange={load} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
